'use client';

import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import { Menu, X, Folder, SquareStack, Briefcase, Users, User as UserIcon, LogOut, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface HeaderProps {
    toggleSidebar: () => void;
}

const Header = ({ toggleSidebar }: HeaderProps) => {
    const { user, logout } = useAuth();

    return (
        <header className="border-b bg-white shadow-sm px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 flex items-center justify-between h-12 sm:h-14 lg:h-16">
            <Link href="/" className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                <img src="/logo.svg" alt="logo" className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8" />
                <span className="font-bold text-base sm:text-lg hidden sm:inline">Negotiation App</span>
            </Link>

            <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                <Button variant="ghost" size="icon" onClick={toggleSidebar} className="md:hidden h-8 w-8">
                    <Menu className="h-4 w-4 sm:h-5 sm:w-5" />
                </Button>

                {user ? (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" className="relative h-7 w-7 sm:h-8 sm:w-8 rounded-full">
                                <UserIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-56" align="end" forceMount>
                            <DropdownMenuLabel className="font-normal">
                                <div className="flex flex-col space-y-1">
                                    <p className="text-sm font-medium leading-none">{user.name}</p>
                                    <p className="text-xs leading-none text-muted-foreground">
                                        {user.email}
                                    </p>
                                </div>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            {user.roles?.includes('admin') && (
                                <Link href="/users">
                                    <DropdownMenuItem>
                                        <Users className="mr-2 h-4 w-4" />
                                        <span>Manage Users</span>
                                    </DropdownMenuItem>
                                </Link>
                            )}
                            <DropdownMenuItem>
                                <Settings className="mr-2 h-4 w-4" />
                                <span>Settings</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={logout}>
                                <LogOut className="mr-2 h-4 w-4" />
                                <span>Log out</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : (
                    <Link href="/login">
                        <Button>Login</Button>
                    </Link>
                )}
            </div>
        </header>
    );
};

export default Header;
